import React, { useState } from 'react';
import { Box, List, ListItem, useTheme, useMediaQuery, Chip, IconButton, Tooltip } from '@mui/material';
import FormattedContent from '../shared/FormattedContent.jsx';
import TypingIndicator from './TypingIndicator.jsx';
import MessageActions from './MessageActions.jsx';
import Icon from '../shared/Icon';

const MessageList = ({ messages, chatEnd, isProcessing = false, showMessageActions = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null);

  // Helper function to submit feedback to the events API
  const submitFeedback = async (messageIndex, feedbackType, feedbackData = null) => {
    try {
      const eventData = {
        type: `message_feedback_${feedbackType}`,
        data: JSON.stringify({
          message_index: messageIndex,
          message_content: messages[messageIndex]?.msg?.substring(0, 200) || '', // First 200 chars for context
          message_role: messages[messageIndex]?.role || 'assistant',
          feedback_type: feedbackType,
          ...(feedbackData && { feedback_details: feedbackData })
        })
      };

      const response = await fetch('/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log(`${feedbackType} feedback submitted successfully for message ${messageIndex}`);
      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return false;
    }
  };

  return (
    <List sx={{
      maxWidth: '100%',
      overflowX: 'hidden',
      fontSize: 'var(--chat-base-font-size)',
    }}>
      {messages.map((msg, index) => {
        const isSystemMessage = msg.user === 'system';
        return (
          <ListItem key={index} alignItems="flex-start" sx={{
            maxWidth: '100%',
            overflowX: 'hidden',
            paddingX: isMobile ? 0 : 2,
          }}>
            {isSystemMessage ? (
              <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                <FormattedContent
                  content={msg.msg || ''}
                  showBorder={false}
                  padding={1}
                  enableEmojiStandardization={true}
                  sx={{
                    padding: '0',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontSize: '1em',
                      marginTop: '0.8em',
                      marginBottom: '0.4em'
                    },
                    '& p': {
                      margin: '0.6rem 0',
                    }
                  }}
                />

                {/* Message Actions */}
                {showMessageActions && (
                  <MessageActions
                    onCopyMessage={async () => {
                      try {
                        await navigator.clipboard.writeText(msg.msg);
                        console.log('Message copied to clipboard');
                      } catch (err) {
                        console.error('Failed to copy message:', err);
                      }
                    }}
                    onPositiveFeedback={async () => {
                      await submitFeedback(index, 'positive');
                    }}
                    onNegativeFeedback={async (feedbackData) => {
                      await submitFeedback(index, 'negative', feedbackData);
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                }}
                onMouseEnter={() => setHoveredMessageIndex(index)}
                onMouseLeave={() => setHoveredMessageIndex(null)}
              >
                {/* Display attached prompts as chips */}
                {msg.metadata?.attachedPrompts && msg.metadata.attachedPrompts.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mb: 0.5,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {msg.metadata.attachedPrompts.map((prompt) => (
                      <Chip
                        key={prompt.id}
                        label={prompt.title}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Display user text if present */}
                {msg.metadata?.userText && (
                  <Box
                    sx={{
                      position: 'relative',
                      maxWidth: '70%',
                    }}
                  >
                    {showMessageActions && (
                      <Tooltip title="Copy Message" arrow placement="left" enterDelay={500}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(msg.metadata.userText);
                              console.log('User message copied to clipboard');
                            } catch (err) {
                              console.error('Failed to copy message:', err);
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            right: 'calc(100% + 0.25rem)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'text.secondary',
                            width: 'var(--chat-action-icon-size-sm)',
                            height: 'var(--chat-action-icon-size-sm)',
                            opacity: hoveredMessageIndex === index ? 'var(--chat-action-icon-opacity)' : 0,
                            transition: 'all 0.2s ease-in-out',
                            pointerEvents: hoveredMessageIndex === index ? 'auto' : 'none',
                            '&:hover': {
                              color: 'primary.main',
                              opacity: '1',
                            },
                          }}
                        >
                          <Icon name="copy" size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box
                      sx={{
                        color: 'var(--user-message-bubble-text)',
                        backgroundColor: 'var(--user-message-bubble-bg)',
                        padding: 'var(--user-message-padding)',
                        borderRadius: 'var(--radius-md)',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {msg.metadata.userText}
                    </Box>
                  </Box>
                )}

                {/* Fallback for messages without metadata (old messages) */}
                {!msg.metadata && (
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: '70%',
                    }}
                  >
                    {showMessageActions && (
                      <Tooltip title="Copy Message" arrow placement="left" enterDelay={500}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(msg.msg);
                              console.log('User message copied to clipboard');
                            } catch (err) {
                              console.error('Failed to copy message:', err);
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            right: 'calc(100% + 0.25rem)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'text.secondary',
                            width: 'var(--chat-action-icon-size-sm)',
                            height: 'var(--chat-action-icon-size-sm)',
                            opacity: hoveredMessageIndex === index ? 'var(--chat-action-icon-opacity)' : 0,
                            transition: 'opacity 0.2s ease-in-out',
                            pointerEvents: hoveredMessageIndex === index ? 'auto' : 'none',
                            '&:hover': {
                              color: 'primary.main',
                              opacity: '1',
                            },
                          }}
                        >
                          <Icon name="copy" size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box
                      sx={{
                        color: 'var(--user-message-bubble-text)',
                        backgroundColor: 'var(--user-message-bubble-bg)',
                        padding: 'var(--user-message-padding)',
                        borderRadius: 'var(--radius-md)',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {msg.msg}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </ListItem>
        );
      })}

      {/* AI Typing Indicator */}
      {isProcessing && (
        <ListItem alignItems="flex-start" sx={{
          maxWidth: '100%',
          overflowX: 'hidden',
          paddingX: isMobile ? 0 : 2,
        }}>
          <TypingIndicator isProcessing={isProcessing} />
        </ListItem>
      )}

      <div ref={chatEnd} />
    </List>
  );
};

export default React.memo(MessageList);
